import { PencilSquareIcon } from '@heroicons/react/20/solid';

import { uniqueIdTransfer } from '@/apiCalls/uniqueIDTransferHelper';

import { EEventType } from '../eventType';

export enum EButtonType {
    PRIMARY = 'PRIMARY',
    ROUNDED_FULL_PRIMARY = 'ROUNDED_FULL_PRIMARY',
    PRIMARY_INVERTED = 'PRIMARY_INVERTED',
    SUCCESS = 'SUCCESS',
    DISABLED = 'DISABLED',
    ROUNDED_DISABLED = 'ROUNDED_DISABLED',
    OUTLINE = 'OUTLINE',
    SECONDARY = 'SECONDARY',
    SECONDARY_OUTLINE = 'SECONDARY_OUTLINE',
    /** Text only, for the lesser of two actions sitting together. */
    SECONDARY_TEXT = 'SECONDARY_TEXT',
    ROUNDED_FULL_SECONDARY_OUTLINE = 'ROUNDED_FULL_SECONDARY_OUTLINE',
    LONGER_ROUNDED_FULL_SECONDARY_OUTLINE = 'LONGER_ROUNDED_FULL_SECONDARY_OUTLINE',
    LONGER_ROUNDED_FULL_PRIMARY = 'LONGER_ROUNDED_FULL_PRIMARY',
    LONGER_ROUNDED_DISABLED = 'LONGER_ROUNDED_DISABLED',
    FULL_ROUNDED_DISABLED = 'FULL_ROUNDED_DISABLED',
    EDIT_BUTTON_OCR_SUMMARY_SECONDARY_OUTLINE = 'EDIT_BUTTON_OCR_SUMMARY_SECONDARY_OUTLINE',
    PRIMARY_DISABLED = 'PRIMARY_DISABLED',
    PRIMARY_OUTLINE = 'PRIMARY_OUTLINE',
    /** Full-width actions inside a card. */
    CARD_PRIMARY = 'CARD_PRIMARY',
    CARD_SECONDARY_OUTLINE = 'CARD_SECONDARY_OUTLINE',
}

export interface IButton {
    children?: React.ReactNode;
    onClick: () => void;
    type?: EButtonType;
    eventType?: EEventType;
    square?: boolean;
    id?: string;
    title?: string;
}

export const testId = 'button';

/**
 * A button component that can be used for any purpose
 *
 * @param {React.ReactNode} children  - Required    - The text or other elements to be displayed in the button
 * @param {function} onClick          - Required    - The function to be called when the button is clicked
 * @param {ButtonType} type           - Optional    - The type of button to be displayed, default is PRIMARY
 *
 * @author Nathan
 * @date 12-09-2022
 * @version 1
 */
export function Button({
    eventType = EEventType.CLICK,
    children,
    onClick,
    type = EButtonType.PRIMARY,
    square,
    id,
    title,
    ...rest
}: IButton): JSX.Element {
    function handleClick(
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): void {
        onClick(); // Call props with component
        // call api
        const id = e.currentTarget.id;
        uniqueIdTransfer({
            eventType: eventType,
            applicationData: { id: id },
        });
    }
    const isInlineEdit =
        type === EButtonType.EDIT_BUTTON_OCR_SUMMARY_SECONDARY_OUTLINE;

    return (
        <button
            id={id}
            data-testid={testId}
            className={`font-bold border text-sm p-1 min-h-[44px] _flex justify-center items-center rounded-full outline-none transition duration-150 ease-out focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1 hover:z-20 ${
                isInlineEdit ? 'w-auto' : 'w-full'
            } ${getButtonTypeClassName(type)} ${
                square && 'rounded-none px-0'
            } ${isInlineEdit ? '' : 'h-8'}`}
            onClick={handleClick}
            disabled={
                type === EButtonType.DISABLED ||
                type === EButtonType.ROUNDED_DISABLED ||
                type === EButtonType.PRIMARY_DISABLED
            }
            {...rest}
        >
            {/* text-lg suits a call to action; a text button is a lesser
                action sitting beside one, so it takes the body size. */}
            <p
                className={
                    type === EButtonType.SECONDARY_TEXT ? 'text-sm' : 'text-lg'
                }
            >
                {title}
            </p>
            {isInlineEdit && (
                <PencilSquareIcon
                    className='mr-1 h-4 w-4 shrink-0'
                    aria-hidden='true'
                />
            )}
            {children}
        </button>
    );
}

export function getButtonTypeClassName(type: EButtonType): string {
    switch (type) {
        case EButtonType.SUCCESS:
            return `px-2 text-white bg-green hover:shadow-lg `;
        case EButtonType.PRIMARY:
            // border-transparent, not no border: the base class sets `border`
            // with no colour, so a filled button picked up Tailwind's default
            // grey as a ring. Dropping the border instead would shrink the
            // button by 2px.
            return `bg-primary border-transparent hover:bg-primaryHover text-white py-2 px-4 rounded-3xl hover:shadow-lg`; // In use
        case EButtonType.PRIMARY_OUTLINE:
            return `bg-transparent text-primary font-semibold border-1 border-solid border border-primary hover:bg-primary hover:text-white py-2 px-4 rounded-3xl hover:shadow-lg`;

        case EButtonType.PRIMARY_INVERTED:
            return ` bg-primary border-primary  text-white px-4 hover:text-primary hover:bg-white focus:text-primaryFocus focus:border-primaryFocus hover:shadow-lg`;
        case EButtonType.PRIMARY_DISABLED:
            return `bg-gray-300 text-gray-500  text-white py-2 px-4 rounded-3xl `;
        case EButtonType.DISABLED:
            return `bg-gray-300 text-gray-500  py-2 px-4 rounded `;
        case EButtonType.ROUNDED_DISABLED:
            return `w-2/5 bg-gray-300 text-gray-500 py-2 px-4 rounded-full `;
        case EButtonType.LONGER_ROUNDED_DISABLED:
            return `w-full bg-gray-300 text-gray-500 py-2 px-4 rounded-full cursor-not-allowed `;
        case EButtonType.FULL_ROUNDED_DISABLED:
            return `w-full bg-gray-300 text-gray-500 py-2 px-4 rounded-full  cursor-not-allowed `;
        case EButtonType.OUTLINE:
            return `bg-transparent hover:bg-primary text-primary hover:text-white py-2 border border-primary hover:border-transparent rounded hover:shadow-lg`;
        case EButtonType.SECONDARY:
            return `bg-secondary hover:bg-secondaryHover text-white py-2 px-4 rounded hover:shadow-lg`;
        case EButtonType.SECONDARY_TEXT:
            return `bg-transparent text-secondary font-semibold border-transparent px-2 hover:underline`;
        case EButtonType.SECONDARY_OUTLINE:
            return `bg-transparent  text-secondary font-semibold  py-2 px-4 border border-secondary  rounded-3xl hover:shadow-lg`;
        case EButtonType.EDIT_BUTTON_OCR_SUMMARY_SECONDARY_OUTLINE:
            return `bg-transparent text-primary font-semibold border-transparent px-2 hover:underline`;
        case EButtonType.ROUNDED_FULL_SECONDARY_OUTLINE:
            return `w-full bg-transparent hover:bg-secondary text-secondary font-semibold hover:text-white py-2 px-4 border border-secondary hover:border-transparent rounded-full hover:shadow-lg`;
        case EButtonType.LONGER_ROUNDED_FULL_SECONDARY_OUTLINE:
            return `w-3/5 bg-transparent hover:bg-secondary text-secondary font-semibold hover:text-white py-2 px-4 border border-secondary hover:border-transparent rounded-full hover:shadow-lg`;
        case EButtonType.CARD_PRIMARY:
            return `bg-primary hover:bg-primaryHover border-transparent text-white`;
        case EButtonType.CARD_SECONDARY_OUTLINE:
            return `bg-white border-referRing text-panel hover:bg-optionTrack`;
        case EButtonType.ROUNDED_FULL_PRIMARY:
            return `w-2/5 bg-primary hover:bg-primaryHover text-white py-2 px-4 rounded-full hover:shadow-lg`;
        case EButtonType.LONGER_ROUNDED_FULL_PRIMARY:
            return `w-3/5 bg-primary hover:bg-primaryHover text-white py-2 px-4 rounded-full hover:shadow-lg`;
        default:
            return '';
    }
}
